import React, { useState } from "react";
import { IoIosArrowForward } from "react-icons/io";
import { useLocation } from "react-router-dom";
import "../../css/Flow.css";
import Header from "../Header/Header";
import Footer from "../Footer/footer";
import { Helmet } from "react-helmet-async";

/* ===== MAIN IMAGES ===== */
import testerImg from "../../../assets/team/tester.png";
import developerImg from "../../../assets/team/developer.png";
import productImg from "../../../assets/team/product.png";
import exploraImg from "../../../assets/team/explora.png";

/* ===== TESTER ICONS ===== */
import t1 from "../../../assets/team/testing/manage_multiple_testing_cycles.svg";
import t2 from "../../../assets/team/testing/Create_structure.svg";
import t3 from "../../../assets/team/testing/log_results_quickly_and_accurately.svg";

/* ===== DEVELOPER ICONS ===== */
import d1 from "../../../assets/team/development/create_and_review_detailed test_cases.svg";
import d2 from "../../../assets/team/development/save_and_reuse_your_test_data.svg";
import d3 from "../../../assets/team/development/customize_and_automate_bug_repot.svg";

/* ===== PRODUCT ICONS ===== */
import p1 from "../../../assets/team/product/requirements.svg";
import p2 from "../../../assets/team/product/issue_taking.svg";
import p3 from "../../../assets/team/product/Dashboard.svg";

/* ===== EXPLORATION ICONS ===== */
import e1 from "../../../assets/team/explor/issue_logging.svg";
import e2 from "../../../assets/team/explor/reproduction_steps.svg";
import e3 from "../../../assets/team/explor/Automated_Reporting.svg";

const teamData = {
  Tester: {
    title: "Streamlining Test Management",
    description: (
      <>
        Testing teams suffer from the constant need to deliver at a higher speed,
        which in most cases leads to disordered test cycles and the quality goals
        being overlooked. Froth TestOps, a robust{" "}
        <span className="highlight">team based test management platform</span>,
        helps to organize and clarify the testing workflows that result from the
        chaos of the teams. It enables the teams to be focused and have control
        over every sprint.
      </>
    ),
    image: testerImg,
    cards: [
      {
        icon: t1,
        title: "Handle different testing cycles smoothly",
        text: "You can manage various test cycles simultaneously without getting confused or losing command of the situation.",
      },
      {
        icon: t2,
        title: "Define structure",
        text: "Arrange your test cases in a way that they can be easily accessed, are clear, and can be reused in different projects.",
      },
      {
        icon: t3,
        title: "Record outcomes quickly and accurately",
        text: "Be very fast during the test execution and at the same time record every detail of it - do not let any results be ​‍​‌‍​‍‌​‍​‌‍​‍‌overlooked.",
      },
    ],
  },

  Developer: {
    title: (
      <>
        <span className="highlight">Froth TestOps</span> For High-Performing
        Development Teams
      </>
    ),
    description: (
      <>
        Development teams are on the verge to deliver smooth and bug-free
        deployments,however, postponements are still happening due to unclear requirements, lack of test cases or data, and defect tracking that is not properly structured.With{" "}
        <span className="highlight">Froth TestOps</span>, development teams get the power of well-planned testing workflows which is the main reason for them to achieve faster sprints, improved collaboration, and elevated release ​‍​‌‍​‍‌​‍​‌‍​‍‌quality.
      </>
    ),
    image: developerImg,
    cards: [
      {
        icon: d1,
        title: "Create and review detailed test cases",
        text: "Construct thorough test cases and work with teammates for quality assurance.",
      },
      {
        icon: d2,
        title: "Save and reuse your test data",
        text: "Keep test cases in a well-organized directory that allows you to reuse them later and maintain consistency.",
      },
      {
        icon: d3,
        title: "Customize and automate bug reporting",
        text: "Adjust your bug reports to fit your needs and set up automatic email updates to make tracking ​‍​‌‍​‍‌​‍​‌‍​‍‌easy.",
      },
    ],
  },

  Product: {
    title: (
      <>
        <span className="highlight">Froth TestOps</span>: A Tool for Product-Led
        Teams to Gain More Power:
      </>
    ),
    description:
      "Product teams are in a constant hurry to meet their deadlines, thus they need to carry out tests rapidly, validate quickly, and make clean releases. Froth TestOps is a perfect fit for product-led teams as it provides them with complete visibility from start to finish. As such, it is a team based test management platform that is a trusted assistant to the management in risk prevention, user feedback handling, and faster delivery of quality ​‍​‌‍​‍‌​‍​‌‍​‍‌experiences.",
    image: productImg,
    cards: [
      {
        icon: p1,
        title: "Requirements",
        text: "Always link tests to requirements for full traceability.",
      },
      {
        icon: p2,
        title: "Issue Tracking",
        text: (
          <>
            Record and keep track of issues locally in <span className="highlight">Froth TestOps</span> or regularly update your external bug tracking tool.
          </>
        ),
      },
      {
        icon: p3,
        title: "Dashboards",
        text: "Know at any moment what is going to be released - or not - with real-time ​‍​‌‍​‍‌​‍​‌‍​‍‌dashboards.",
      },
    ],
  },

  Exploration: {
    title: (
      <>
        Smart Solutions with{" "}
        <span className="highlight">Froth TestOps</span>
      </>
    ),
    description: (
      <>
        Not every team has a strict testing process in place – and that’s perfectly fine. Exploratory testing enables testers to think like users, bend the rules, and identify “hidden gems” sooner. With the help of {" "}
        "<span className="highlight">Froth TestOps</span>", these teams can easily benefit from an effective “defect management” module, allowing them to instantly begin testing, note bugs, and track their progress.
      </>
    ),
    image: exploraImg,
    cards: [
      {
        icon: e1,
        title: "Issue Logging",
        text: "Capture and log defects instantly with precision.",
      },
      {
        icon: e2,
        title: "Reproduction Steps",
        text: "Document detailed step-by-step instructions to easily reproduce a issue.",
      },
      {
        icon: e3,
        title: "Automated Reporting",
        text: "Generate custom reports and schedule them to be shared with your team automatically — keeping everyone aligned.",
      },
    ],
  },
};

const Flow = () => {
  const [selectedTeam, setSelectedTeam] = useState("Tester");
  const location = useLocation();
  const { title, description, image, cards } = teamData[selectedTeam];

  return (
    <>
      <Helmet>
        <title>
          Team Based Test Management Platform for Efficient QA
        </title>

        <meta
          name="description"
          content="Boost collaboration and efficiency with our team based test management platform, streamlining QA, test execution, and reporting for modern software teams."
        />

        <meta
          name="keywords"
          content="team based test management platform"
        />

        <link
          rel="canonical"
          href="https://frothtestops.com/byteam"
        />

        {/* Open Graph Tags */}
        <meta property="og:title" content="Team Based Test Management Platform for Efficient QA" />
        <meta property="og:description" content="Boost collaboration and efficiency with our team based test management platform, streamlining QA, test execution, and reporting for modern software teams." />
        <meta property="og:image" content="/images/og-image.jpg" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://frothtestops.com/byteam" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Team Based Test Management Platform for Efficient QA" />
        <meta name="twitter:description" content="Boost collaboration and efficiency with our team based test management platform, streamlining QA, test execution, and reporting for modern software teams." />
        <meta name="twitter:image" content="/images/og-image.jpg" />
      </Helmet>
    <div className={`fd-main-wrapper ${location.pathname !== "/" ? "page-padding" : ""}`}>
      <Header />
      <div className="flow-container">
        <h1 className="flow-title">Solution</h1>

      <div className="flow-grid">

        {/* LEFT */}
        <div className="flow-sidebar">
          <div>
            <h2>By Team</h2>

            {Object.keys(teamData).map((team) => (
              <button
                key={team}
                onClick={() => setSelectedTeam(team)}
                className={`flow-btn ${selectedTeam === team ? "active" : ""}`}
              >
                <span>{team}</span>
                <IoIosArrowForward />
              </button>
            ))}
          </div>
        </div>

        {/* RIGHT */}
        <div className="flow-content">

          <div>
            <h2>{title}</h2>
            <p>{description}</p>
          </div>

          <div className="flow-image">
            <img src={image} alt={selectedTeam} />
          </div>

          <div className="flow-cards">
            {cards.map((card, i) => (
              <div key={i} className="flow-card">
                <img src={card.icon} alt="" />
                <h4>{card.title}</h4>
                <p>{card.text}</p>
              </div>
            ))}
          </div>

        </div>

      </div>
      </div>
      <Footer />
    </div>
    </>
  );
};

export default Flow;